import { Injectable } from "@angular/core";
import { AppSettings } from "app/app-settings";

export interface MessageData {
  iconUrl: string;
  heading: string;
  message: string;
}

@Injectable({
  providedIn: "root",
})
export class StreakMessageService {
  // Base paths for icons - same as original service
  private readonly iconPaths = {
    none: "",
    sprout: AppSettings.cdnUrl + "assets/images/ico-plant.svg",
    fire: AppSettings.cdnUrl + "assets/images/ico-fire-big.svg",
    star: AppSettings.cdnUrl + "assets/images/ico-star.svg",
    chart: AppSettings.cdnUrl + "assets/images/ico-progress.svg",
    warning: AppSettings.cdnUrl + "assets/images/ico-warning-big.svg",
    alert: AppSettings.cdnUrl + "assets/images/ico-alarming.svg",
  };

  constructor() {}

  /**
   * Get streak message data based on consistency, diligence, goal completion, and day
   * @param consistency Percentage of days attended (0-100)
   * @param diligence Percentage of committed time completed (0-100)
   * @param goalCompletion Percentage of daily goals achieved (0-100)
   * @param day Day number (1-7)
   */
  getStreakMessage(
    consistency: number,
    diligence: number,
    goalCompletion: number,
    day: number
  ): MessageData {
    // Validate inputs
    if (day < 1 || day > 7) {
      throw new Error("Day must be between 1 and 7");
    }

    // Get icon URL based on diligence range (same logic as original service)
    const iconUrl = this.getIconUrl(diligence, day);

    // Get message based on day, consistency, diligence, and goal completion
    const { heading, message } = this.getMessage(
      day,
      consistency,
      diligence,
      goalCompletion
    );

    console.log({ iconUrl, heading, message });
    return { iconUrl, heading, message };
  }

  /**
   * Get the appropriate icon URL based on diligence
   * Using the same logic as the original service
   */
  private getIconUrl(diligence: number, day: number): string {
    if (day === 1) {
      return this.iconPaths.sprout;
    } else if (diligence >= 100) {
      return this.iconPaths.fire;
    } else if (diligence >= 75 && diligence <= 99) {
      return this.iconPaths.star;
    } else if (diligence >= 56 && diligence <= 74) {
      return this.iconPaths.chart;
    } else if (diligence >= 45 && diligence <= 55) {
      return this.iconPaths.warning;
    } else if (diligence < 45) {
      return this.iconPaths.alert;
    } else {
      return this.iconPaths.none;
    }
  }

  /**
   * Get message data based on day, consistency, diligence, and goal completion
   */
  private getMessage(
    day: number,
    consistency: number,
    diligence: number,
    goalCompletion: number
  ): { heading: string; message: string } {
    switch (day) {
      case 1:
        return this.getDayOneMessage();
      case 2:
        return this.getDayTwoMessage(consistency, diligence, goalCompletion);
      case 3:
        return this.getDayThreeMessage(consistency, diligence, goalCompletion);
      case 4:
        return this.getDayFourMessage(consistency, diligence, goalCompletion);
      case 5:
        return this.getDayFiveMessage(consistency, diligence, goalCompletion);
      case 6:
        return this.getDaySixMessage(consistency, diligence, goalCompletion);
      case 7:
        return this.getDaySevenMessage(consistency, diligence, goalCompletion);
      default:
        return {
          heading: "Keep Going!",
          message:
            "Continue your study momentum and work towards your learning goals.",
        };
    }
  }
  getWeeklySummaryMessage(
    consistency: number,
    diligence: number,
    goalCompletion: number
  ): MessageData {
    // Get icon URL based on diligence - use highest achievement icon for summary
    let iconUrl = this.iconPaths.none;
    if (consistency === 100 && diligence === 100 && goalCompletion >= 75) {
      iconUrl = this.iconPaths.fire; // Perfect week
    } else if (consistency >= 85 && diligence >= 75) {
      iconUrl = this.iconPaths.star; // Very strong week
    } else if (consistency >= 70 && diligence >= 56) {
      iconUrl = this.iconPaths.chart; // Good week
    } else if (consistency >= 50 && diligence >= 45) {
      iconUrl = this.iconPaths.warning; // Concerning week
    } else {
      iconUrl = this.iconPaths.alert; // Poor week
    }

    // Get weekly summary message based on consistency, diligence, and goal completion
    console.log(consistency, diligence, goalCompletion);
    const { heading, message } = this.getWeeklySummary(
      consistency,
      diligence,
      goalCompletion
    );

    return { iconUrl, heading, message };
  }

  private getDayOneMessage(): { heading: string; message: string } {
    return {
      heading: "New Week, New Heights!",
      message:
        "Ready to start strong this week? <b>Commit to your study goals</b> and watch your progress grow. Let's <b>make every hour count</b>!",
    };
  }

  private getDayTwoMessage(
    consistency: number,
    diligence: number,
    goalCompletion: number
  ): { heading: string; message: string } {
    if (consistency === 100) {
      if (diligence >= 75) {
        if (goalCompletion === 100) {
          return {
            heading: "Perfect Start!",
            message:
              "<b>Great job completing all your study goals</b> yesterday! You're building <b>strong momentum</b> for the week. Keep this excellent work going!",
          };
        } else {
          // goalCompletion < 100
          return {
            heading: "Strong Beginning, Daily Goal Focus Needed",
            message:
              "<b>Good job studying a lot</b> yesterday! Your total hours look great, but focus on <b>hitting your daily targets</b> to build better study habits.",
          };
        }
      } else if (diligence >= 45) {
        if (goalCompletion >= 75) {
          return {
            heading: "Goal-Oriented Start!",
            message:
              "<b>Good work showing up and hitting your daily target</b> yesterday! While your overall hours can improve, your ability to meet daily goals shows <b>great discipline</b>. Keep it up!",
          };
        } else {
          return {
            heading: "Solid First Step!",
            message:
              "You <b>showed up to study</b> yesterday - that's a good start! You did more than half your planned time, but try to <b>complete your full daily goal</b> today. Small improvements add up!",
          };
        }
      } else {
        // diligence < 45
        return {
          heading: "Room for Growth",
          message:
            "You <b>started studying</b> this week - that's positive! However, you completed <b>less than half your planned time</b>. Today is your chance to increase your focus and study more.",
        };
      }
    } else {
      // consistency === 0
      return {
        heading: "Missed Start - Act Now",
        message:
          "You <b>didn't study yesterday</b>. Don't let this become a pattern! The beginning of your week is <b>crucial for momentum</b>. Make today count by following your study schedule.",
      };
    }
  }

  private getDayThreeMessage(
    consistency: number,
    diligence: number,
    goalCompletion: number
  ): { heading: string; message: string } {
    if (consistency === 100) {
      if (diligence >= 75) {
        if (goalCompletion === 100) {
          return {
            heading: "Perfect Streak!",
            message:
              "Two days of <b>perfect attendance</b> and <b>hitting all your goals</b>! You're crushing it. This consistency leads to breakthrough learning moments. Keep this pattern going!",
          };
        } else if (goalCompletion >= 45) {
          return {
            heading: "Strong Hours, Target Precision Needed",
            message:
              "You've <b>shown up both days</b> with <b>substantial study time</b>. Your hours are impressive, but try to hit your exact daily targets more consistently for optimal learning.",
          };
        } else {
          return {
            heading: "Nearly Perfect Hours, Daily Goals Need Work",
            message:
              "Your <b>attendance and study time are excellent</b>! However, you're rarely hitting your specific daily targets. Consider <b>adjusting how you distribute your study time</b> throughout the week.",
          };
        }
      } else if (diligence >= 45) {
        if (goalCompletion >= 75) {
          return {
            heading: "Goal-Focused Approach!",
            message:
              "<b>Perfect attendance</b> so far and you're <b>hitting your daily targets</b>! While your overall hours can improve, your precision in meeting goals shows excellent discipline. This approach will yield strong results.",
          };
        } else if (goalCompletion >= 45) {
          return {
            heading: "Consistent Presence!",
            message:
              "<b>Perfect attendance</b> so far! You're studying more than half your committed time and hitting your targets about half the time. <b>Push yourself more</b> on your study hours and daily goal precision.",
          };
        } else {
          return {
            heading: "Attendance Strong, Daily Goals Slipping",
            message:
              "You've <b>shown up consistently</b>, which is great! While your study time is moderate, you're rarely meeting your specific daily targets. Focus on <b>better aligning your study sessions</b> with your planned goals.",
          };
        }
      } else {
        // diligence < 45
        return {
          heading: "Consistency at Risk",
          message:
            "While you've shown up both days, your <b>study time is well below your targets</b>. This pattern can hurt your progress. <b>Today is crucial</b> - commit to your full study hours to get back on track.",
        };
      }
    } else if (consistency === 50) {
      if (diligence >= 75) {
        if (goalCompletion === 100) {
          return {
            heading: "Mixed Start - Great Intensity",
            message:
              "You missed one day but <b>gave 100% effort</b> when you did study and hit your daily target perfectly. <b>Consistency is key</b> to success. Aim for perfect attendance while maintaining that excellent intensity!",
          };
        } else {
          return {
            heading: "Building Back Momentum",
            message:
              "You missed one day but <b>put in good effort</b> when you studied, though you didn't fully hit your daily target. Today is your chance to <b>establish a consistent pattern</b> while improving your daily precision.",
          };
        }
      } else if (diligence >= 45) {
        return {
          heading: "Attendance and Hours Need Work",
          message:
            "Your week has been <b>inconsistent so far</b> - both in showing up and meeting your hourly commitments. <b>Today can be your turning point</b>. Commit to full attendance and stronger study sessions.",
        };
      } else {
        return {
          heading: "Warning: Week at Risk",
          message:
            "You've missed one day and studied <b>less than half your committed hours</b> when you did show up. This seriously impacts your progress. <b>It's time for a reset</b> - commit fully starting today.",
        };
      }
    } else {
      // consistency === 0
      return {
        heading: "Critical Alert: Zero Progress",
        message:
          "<b>Two days have passed without any study time.</b> This will significantly impact your learning goals if it continues. <b>Today requires immediate action</b> - show up and put in your full committed hours.",
      };
    }
  }

  private getDayFourMessage(
    consistency: number,
    diligence: number,
    goalCompletion: number
  ): { heading: string; message: string } {
    if (consistency === 100) {
      if (diligence >= 75) {
        if (goalCompletion >= 75) {
          return {
            heading: "Flawless Performance!",
            message:
              "Three days of <b>perfect attendance</b> with <b>good study hours and consistent goal achievement</b>. This commitment will lead to good results.",
          };
        } else if (goalCompletion >= 45) {
          return {
            heading: "Strong Overall, Daily Balance Improving",
            message:
              "You <b>haven't missed a day</b> and your <b>study time is strong</b>. You're hitting your daily targets more than half the time - creating good learning conditions. Keep refining that daily precision.",
          };
        } else {
          return {
            heading: "Nearly Perfect Streak, Target Alignment Needed",
            message:
              "You <b>haven't missed a day</b> and your <b>study time is strong</b>. Better distribute your hours to hit more daily targets and your learning routine will improve. Good work so far.",
          };
        }
      } else if (diligence >= 45) {
        if (goalCompletion >= 75) {
          return {
            heading: "Daily Goal Champion!",
            message:
              "You've <b>shown up every day</b> and <b>consistently hit your daily targets</b>. Your overall study volume can increase, but your approach to daily goals is creating steady progress.",
          };
        } else if (goalCompletion >= 45) {
          return {
            heading: "Perfect Attendance, Hours Need Focus",
            message:
              "You've <b>shown up every day</b>. Your study hours and goal achievement are moderate. <b>Push yourself today</b> to hit your full time commitment and daily target.",
          };
        } else {
          return {
            heading: "Attendance Strong, Goal Precision Concerning",
            message:
              "While you <b>haven't missed a day</b>, you're rarely hitting your daily targets. This creates an imbalance. Today, <b>focus on aligning your study time</b> with your daily goal.",
          };
        }
      } else {
        // diligence < 45
        return {
          heading: "Warning: Hours Critically Low",
          message:
            "Your perfect attendance is undermined by <b>minimal study hours</b> and missed targets. This limits your progress. <b>Today is crucial</b> - commit to your full study hours.",
        };
      }
    } else if (consistency >= 67) {
      if (diligence >= 75) {
        if (goalCompletion >= 75) {
          return {
            heading: "Rebuilding Momentum",
            message:
              "You missed one day but <b>gave full effort</b> when you studied and hit your targets. We're at mid-week - <b>recommit to perfect attendance</b> while maintaining your study intensity.",
          };
        } else {
          return {
            heading: "Getting Back on Track",
            message:
              "Despite missing one day, your <b>study quality is good</b> when you show up, though daily goal precision varies. For the rest of the week, <b>aim for perfect attendance</b>.",
          };
        }
      } else if (diligence >= 45) {
        return {
          heading: "Mixed Performance",
          message:
            "Your week shows <b>inconsistent patterns</b> in attendance, study hours, and goal achievement. <b>The second half of your week is crucial</b>. Commit to showing up every day.",
        };
      } else {
        return {
          heading: "Warning: Performance Slipping",
          message:
            "You've missed one day and your <b>study hours are falling short</b>. This puts your weekly goals at risk. <b>Today is your chance to reset</b> with full hours.",
        };
      }
    } else if (consistency >= 33) {
      if (diligence >= 75) {
        return {
          heading: "Critical: Attendance Needs Focus",
          message:
            "You've <b>missed two days</b>, though your effort was good when you did study. We're at mid-week - your <b>attendance requires immediate attention</b>.",
        };
      } else {
        return {
          heading: "Urgent Intervention Needed",
          message:
            "Your week is <b>off course</b> with multiple missed days and inadequate study hours. This will impact your progress. <b>Today requires a reset</b> and full commitment.",
        };
      }
    } else {
      // consistency < 33
      return {
        heading: "Critical Alert: Zero Progress",
        message:
          "<b>Half the week has passed without any study time.</b> This will impact your learning goals. <b>Today requires immediate action</b> - show up and put in your hours.",
      };
    }
  }
  private getDayFiveMessage(
    consistency: number,
    diligence: number,
    goalCompletion: number
  ): { heading: string; message: string } {
    if (consistency === 100) {
      if (diligence >= 75) {
        if (goalCompletion >= 75) {
          return {
            heading: "Exceptional Consistency!",
            message:
              "<b>Four perfect days in a row</b>. Your discipline in attendance, study hours, and goal achievement is creating <b>good learning momentum</b>. You're set for good results this week.",
          };
        } else if (goalCompletion >= 45) {
          return {
            heading: "Strong Week, Daily Precision Improving",
            message:
              "<b>Perfect attendance and good study hours</b>. You're hitting your daily targets more than half the time, showing progress in balancing your approach. This is <b>effective for learning</b>.",
          };
        } else {
          return {
            heading: "Outstanding Commitment!",
            message:
              "<b>Perfect attendance and good study hours</b>. Focus on better distributing your time to hit more daily targets for <b>better learning efficiency</b>.",
          };
        }
      } else if (diligence >= 45) {
        if (goalCompletion >= 75) {
          return {
            heading: "Perfect Attendance, Daily Goal Champion!",
            message:
              "You <b>haven't missed a day</b> and consistently hit your daily targets. While your overall hours can grow, your approach to meeting daily goals <b>creates steady progress</b>.",
          };
        } else if (goalCompletion >= 45) {
          return {
            heading: "Attendance Champion!",
            message:
              "You <b>haven't missed a day</b>. Your study hours and goal achievement show room for improvement. <b>Push yourself today</b> to increase your focus time.",
          };
        } else {
          return {
            heading: "Attendance Perfect, Hours Concerning",
            message:
              "While you've <b>shown up every day</b>, you're rarely hitting your daily targets. This creates an imbalance. Today, <b>focus on aligning your study time</b> with your daily goal.",
          };
        }
      } else {
        // diligence < 45
        return {
          heading: "Warning: Critical Study Deficit",
          message:
            "Your <b>perfect attendance</b> is undermined by minimal study hours and missed targets. This <b>limits your outcomes</b>. Today, commit to your full study hours.",
        };
      }
    } else if (consistency >= 75) {
      if (diligence >= 75) {
        if (goalCompletion >= 75) {
          return {
            heading: "Strong Recovery Potential",
            message:
              "Despite missing one day, your <b>study quality is good</b> when you show up and you're hitting daily targets. <b>Maintain perfect attendance</b> and keep focused for the weekend.",
          };
        } else {
          return {
            heading: "Solid Performance",
            message:
              "You've <b>shown up most days</b> with good study hours, though daily goal precision varies. For the weekend, <b>aim for perfect attendance</b> while fine-tuning your approach.",
          };
        }
      } else if (diligence >= 45) {
        return {
          heading: "Weekend Focus Needed",
          message:
            "Your <b>attendance and study hours need improvement</b>, with inconsistent goal achievement. The weekend is your opportunity to improve. <b>Commit to showing up</b> with increased focus.",
        };
      } else {
        return {
          heading: "Critical Study Quality Issue",
          message:
            "While your <b>attendance is consistent</b>, your study quality is concerning. <b>Minimal hours create learning gaps</b>. The weekend requires your full commitment to quality study time.",
        };
      }
    } else if (consistency >= 50) {
      if (diligence >= 75) {
        return {
          heading: "Halfway There - Strong When Present",
          message:
            "You've <b>missed half the week</b>, though your effort was good when you did study. The weekend is crucial - your <b>attendance requires immediate attention</b>.",
        };
      } else if (diligence >= 45) {
        return {
          heading: "Weekend Recovery Critical",
          message:
            "With <b>half the week missed</b>, your progress is at risk despite moderate study quality when present. The weekend requires <b>perfect attendance and improved study quality</b>.",
        };
      } else {
        return {
          heading: "Severe Learning Deficit",
          message:
            "<b>Half the week missed</b> and minimal study effort when present creates a learning deficit. <b>Full commitment</b> is necessary for the weekend.",
        };
      }
    } else if (consistency >= 25) {
      if (diligence >= 75) {
        return {
          heading: "Critical: Week Mostly Missed",
          message:
            "You've <b>missed most of the week</b> despite good effort on your one study day. The weekend is your <b>final opportunity</b> to show commitment.",
        };
      } else if (diligence >= 45) {
        return {
          heading: "Critical Learning Gap",
          message:
            "Your week shows <b>minimal engagement</b> with moderate study quality when present. This creates learning deficits. The weekend <b>requires full commitment</b>.",
        };
      } else {
        return {
          heading: "Critical Intervention Required",
          message:
            "Your week shows <b>almost complete disengagement</b> in showing up and study quality. The weekend is your <b>last chance</b> to show any commitment.",
        };
      }
    } else {
      // consistency < 25
      return {
        heading: "Urgent: Zero Progress Week",
        message:
          "<b>Most of the week has passed without any study time</b>. This will impact your learning goals. The weekend <b>requires immediate action</b> and commitment.",
      };
    }
  }

  private getDaySixMessage(
    consistency: number,
    diligence: number,
    goalCompletion: number
  ): { heading: string; message: string } {
    if (consistency === 100) {
      if (diligence >= 75) {
        if (goalCompletion >= 75) {
          return {
            heading: "Perfect Week Within Reach!",
            message:
              "<b>Five days in a row</b> with good attendance, study hours, and daily targets. <b>Just one more day</b> to complete a strong week of learning.",
          };
        } else if (goalCompletion >= 45) {
          return {
            heading: "Excellence in Consistency!",
            message:
              "<b>Perfect attendance</b> with good study hours. Your daily target achievement is improving. <b>Maintain this pattern</b> for one more day to complete a good week.",
          };
        } else {
          return {
            heading: "Attendance Champion!",
            message:
              "Your <b>perfect attendance and good study time</b> have been consistent. For your final weekday, <b>focus on aligning</b> your study session with your daily target to round out your week.",
          };
        }
      } else if (diligence >= 45) {
        if (goalCompletion >= 75) {
          return {
            heading: "Perfect Attendance, Goal Champion!",
            message:
              "You <b>haven't missed a day</b> and consistently hit your daily targets. For your final weekday, <b>maintain this approach</b> while increasing your overall study volume.",
          };
        } else if (goalCompletion >= 45) {
          return {
            heading: "Attendance Champion!",
            message:
              "You <b>haven't missed a day</b> - good discipline. Your study hours and goal achievement have been steady. For your final weekday, <b>try to increase your focus time</b>.",
          };
        } else {
          return {
            heading: "Perfect Attendance, Hours Need Work",
            message:
              "While you've <b>shown up every day</b>, you're not consistently hitting your daily targets. For your final weekday, <b>focus on aligning</b> your study session with your daily goal.",
          };
        }
      } else {
        // diligence < 45
        return {
          heading: "Warning: Hours Critically Low",
          message:
            "Your <b>perfect attendance</b> is undermined by minimal study hours and missed targets. This limits your learning. <b>Make your final weekday count</b> with full study time aligned to your goal.",
        };
      }
    } else if (consistency >= 80) {
      if (diligence >= 75) {
        if (goalCompletion >= 75) {
          return {
            heading: "Strong Week, One Day To Go",
            message:
              "Despite missing one day, your <b>study quality is good</b> when you show up and you're hitting your targets. <b>Complete your week</b> with attendance tomorrow and maintain your focused approach.",
          };
        } else {
          return {
            heading: "Solid Week Nearing Completion",
            message:
              "You've <b>shown up most days</b> with good study hours, though daily goal precision varies. <b>Finish strong</b> with attendance and a focused approach to hitting your final target.",
          };
        }
      } else if (diligence >= 45) {
        return {
          heading: "Final Weekday Focus Needed",
          message:
            "Your <b>attendance and study hours can improve</b>, with varied success hitting targets. <b>Make your final weekday count</b> with increased study focus to strengthen your weekly performance.",
        };
      } else {
        return {
          heading: "Critical: Study Quality Issue",
          message:
            "While your <b>attendance is mostly consistent</b>, your study quality needs work. Minimal hours create learning gaps. <b>Make your final weekday count</b> with full, quality study time.",
        };
      }
    } else if (consistency >= 60) {
      if (diligence >= 75) {
        return {
          heading: "Weekend Recovery Critical",
          message:
            "You've <b>missed two days</b>, though your effort was good when you did study. Your final weekday is <b>crucial</b> - show up and maintain your study intensity.",
        };
      } else if (diligence >= 45) {
        return {
          heading: "Concerning Patterns This Week",
          message:
            "<b>Missing days and moderate study hours</b> creates learning gaps. Your final weekday is your chance to <b>establish a stronger pattern</b> before the weekend.",
        };
      } else {
        return {
          heading: "Critical: Learning Outcomes at Risk",
          message:
            "<b>Missed days and minimal study effort</b> create a learning deficit. Your final weekday <b>requires full commitment</b> to quality study time.",
        };
      }
    } else if (consistency >= 40) {
      if (diligence >= 75) {
        return {
          heading: "Critical: Most Days Missed",
          message:
            "You've <b>missed most weekdays</b> despite good effort when you did study. Your final weekday and weekend are your <b>last opportunity</b> to show commitment to your learning.",
        };
      } else if (diligence >= 45) {
        return {
          heading: "Severe Consistency Problem",
          message:
            "Your week shows <b>minimal engagement</b> with moderate study quality. This creates learning deficits. Your <b>final weekday requires full commitment</b>.",
        };
      } else {
        return {
          heading: "Urgent Intervention Required",
          message:
            "Your week shows <b>almost complete disengagement</b> in both attendance and quality. Your final weekday is your <b>last chance</b> to show any commitment to learning.",
        };
      }
    } else if (consistency >= 20) {
      if (diligence >= 75) {
        return {
          heading: "Critical Alert: Week Nearly Lost",
          message:
            "You've <b>missed nearly all weekdays</b> despite good effort on your one study day. Your final weekday is your <b>last opportunity</b> to show any commitment.",
        };
      } else if (diligence >= 45) {
        return {
          heading: "Urgent: Week Recovery Needed",
          message:
            "Your week shows <b>minimal engagement</b> with moderate study quality on your one day present. Your <b>final weekday requires full commitment</b>.",
        };
      } else {
        return {
          heading: "Severe Learning Risk",
          message:
            "Your week shows <b>almost complete disengagement</b> in both showing up and study quality. Your final weekday is your <b>last chance</b> to show any commitment.",
        };
      }
    } else {
      // consistency < 20
      return {
        heading: "Critical: Zero Progress Week",
        message:
          "<b>The entire week has passed without any study time</b>. This will impact your learning goals. Your final weekday <b>requires immediate action</b> and commitment.",
      };
    }
  }
  private getDaySevenMessage(
    consistency: number,
    diligence: number,
    goalCompletion: number
  ): { heading: string; message: string } {
    if (consistency === 100) {
      if (diligence >= 75) {
        if (goalCompletion >= 75) {
          return {
            heading: "Perfect Week Within Reach!",
            message:
              "<b>Six days in a row</b> with good attendance, study hours, and daily targets. <b>One more day</b> to complete a strong week of learning.",
          };
        } else if (goalCompletion >= 45) {
          return {
            heading: "Excellence in Consistency!",
            message:
              "<b>Perfect attendance</b> with good study hours. Your daily target achievement is solid. <b>Maintain this pattern</b> for one more day to complete a good week.",
          };
        } else {
          return {
            heading: "Attendance Champion!",
            message:
              "Your <b>perfect attendance and good study time</b> have been consistent. For your final day, <b>focus on hitting your daily target</b> to round out your week with precision.",
          };
        }
      } else if (diligence >= 45) {
        if (goalCompletion >= 75) {
          return {
            heading: "Perfect Attendance, Goal Master!",
            message:
              "You <b>haven't missed a day</b> and consistently hit your daily targets. For your final day, <b>maintain this approach</b> while increasing your overall study volume.",
          };
        } else if (goalCompletion >= 45) {
          return {
            heading: "Attendance Champion!",
            message:
              "You <b>haven't missed a day</b>. Your study hours and goal achievement have been steady. For your final day, <b>try to increase your focus time</b>.",
          };
        } else {
          return {
            heading: "Perfect Attendance, Hours Need Work",
            message:
              "While you've <b>shown up every day</b>, you're rarely hitting your daily targets. For your final day, <b>focus on aligning your study session</b> with your daily goal.",
          };
        }
      } else {
        // diligence < 45
        return {
          heading: "Warning: Hours Critically Low",
          message:
            "Your perfect attendance is undermined by <b>minimal study hours</b> and missed targets. This limits your learning. <b>Make your final day count</b> with full study time aligned to your goal.",
        };
      }
    } else if (consistency >= 83) {
      if (diligence >= 75) {
        if (goalCompletion >= 75) {
          return {
            heading: "Strong Week, Final Day",
            message:
              "Despite missing one day, your <b>study quality is good</b> when you show up and you're hitting targets consistently. <b>Complete your week</b> with attendance today and maintain your focus.",
          };
        } else {
          return {
            heading: "Solid Week Nearing Completion",
            message:
              "You've <b>shown up most days</b> with good study hours, though daily goal precision varies. <b>Finish strong</b> with attendance and a focused approach to hitting your final target.",
          };
        }
      } else if (diligence >= 45) {
        return {
          heading: "Final Day Focus Needed",
          message:
            "Your <b>attendance and study hours can improve</b>, with varied success hitting targets. <b>Make your final day count</b> with increased study focus to strengthen your weekly performance.",
        };
      } else {
        return {
          heading: "Critical: Study Quality Issue",
          message:
            "While your <b>attendance is mostly consistent</b>, your study quality needs work. Minimal hours create learning gaps. <b>Make your final day count</b> with full, quality study time.",
        };
      }
    } else if (consistency >= 67) {
      if (diligence >= 75) {
        return {
          heading: "Mixed Week - Strong Finish Needed",
          message:
            "You've <b>missed two days</b>, though your effort was good when you did study. Your <b>final day is crucial</b> - show up and maintain your study intensity to improve your week's outcome.",
        };
      } else if (diligence >= 45) {
        return {
          heading: "Concerning Patterns This Week",
          message:
            "<b>Missing days and moderate study hours</b> creates learning gaps. Your final day is your chance to <b>end on a strong note</b> despite the inconsistency.",
        };
      } else {
        return {
          heading: "Critical: Learning Outcomes at Risk",
          message:
            "<b>Missed days and minimal study effort</b> create a learning deficit. Your final day <b>requires full commitment</b> to quality study time.",
        };
      }
    } else if (consistency >= 50) {
      if (diligence >= 75) {
        return {
          heading: "Half Week Missed - Final Chance",
          message:
            "You've <b>missed half the week</b> despite good effort when you did study. Your final day is your <b>last opportunity</b> to show commitment to your learning goals.",
        };
      } else if (diligence >= 45) {
        return {
          heading: "Serious Consistency Problem",
          message:
            "Your week shows <b>poor engagement</b> with moderate study quality. This creates learning deficits. Your <b>final day requires full commitment</b>.",
        };
      } else {
        return {
          heading: "Urgent Intervention Required",
          message:
            "Your week shows <b>major disengagement</b> in both attendance and quality. Your final day is your <b>last chance</b> to show any commitment to learning.",
        };
      }
    } else if (consistency >= 33) {
      if (diligence >= 75) {
        return {
          heading: "Critical Alert: Week Mostly Lost",
          message:
            "You've <b>missed most of the week</b> despite good effort on your study days. Your final day is your <b>last opportunity</b> to show any consistent commitment.",
        };
      } else if (diligence >= 45) {
        return {
          heading: "Critical Alert: Week Mostly Lost",
          message:
            "You've <b>missed most of the week</b> despite good study hours on your days present. Your final day is your <b>last opportunity</b> to show commitment.",
        };
      } else {
        return {
          heading: "Critical Alert: Week Mostly Lost",
          message:
            "You've <b>missed most of the week</b>, and while your hours are good when present, you're rarely hitting your targets. Your final day requires <b>both presence and better alignment</b> with your goals.",
        };
      }
    } else if (consistency >= 17) {
      if (diligence >= 75) {
        return {
          heading: "Critical: Week Nearly Lost",
          message:
            "You've <b>missed nearly all week</b> despite good effort on your one study day. Your final day is your <b>last opportunity</b> to show any commitment.",
        };
      } else if (diligence >= 45) {
        return {
          heading: "Critical: Week Nearly Lost",
          message:
            "You've <b>missed nearly all week</b> despite good study hours on your one day present. Your final day is your <b>last opportunity</b> to show commitment.",
        };
      } else {
        return {
          heading: "Critical: Week Nearly Lost",
          message:
            "You've <b>missed nearly all week</b>, and you didn't hit your daily target on your one day. Your final day requires <b>both presence and better alignment</b> with your specific goals.",
        };
      }
    } else {
      // consistency < 17
      return {
        heading: "Critical: Zero Progress Week",
        message:
          "<b>You didn't study at all this week. </b>If this continues, it will seriously harm your learning goals. For next week, you need to start fresh and recommit to your studies.",
      };
    }
  }
  private getWeeklySummary(
    consistency: number,
    diligence: number,
    goalCompletion: number
  ): { heading: string; message: string } {
    // Perfect week - 100% consistency (7/7)
    if (consistency === 100) {
      if (diligence >= 100) {
        if (goalCompletion >= 75) {
          return {
            heading: "Perfect Week Achieved!",
            message:
              "Great performance! <b>Perfect attendance, perfect study hours</b>, and consistent daily goal achievement every day. This commitment sets you up for learning success. Keep building on this foundation.",
          };
        } else if (goalCompletion >= 45) {
          return {
            heading: "Amazing Week Achieved!",
            message:
              "Great performance! <b>Perfect attendance and perfect study hours</b> every day, with good progress on daily targets. Focus on fine-tuning your daily goal precision for next week.",
          };
        } else {
          return {
            heading: "Near-Perfect Week!",
            message:
              "<b>Perfect attendance and study hours</b> throughout the week! To improve, focus on better distributing your study time to hit your daily targets. Now let's optimize your learning efficiency.",
          };
        }
      } else if (diligence >= 75) {
        if (goalCompletion >= 75) {
          return {
            heading: "Near-Perfect Week!",
            message:
              "Good performance with <b>perfect attendance, strong study hours</b>, and good daily goal achievement. Next week, aim to hit your full hourly targets for even better results.",
          };
        } else if (goalCompletion >= 45) {
          return {
            heading: "Near-Perfect Week!",
            message:
              "Good consistency with <b>perfect attendance and strong study hours</b>. Your daily goal achievement was variable. Next week, focus on consistent daily targets while maintaining your overall approach.",
          };
        } else {
          return {
            heading: "Excellence with Room to Balance",
            message:
              "<b>Perfect attendance with good study hours</b>. To improve your learning, focus on better distributing your study time to hit more of your daily targets.",
          };
        }
      } else if (diligence >= 56) {
        if (goalCompletion >= 75) {
          return {
            heading: "Perfect Attendance, Goal Master!",
            message:
              "You <b>showed up every day and consistently hit your daily targets</b>. Focus on increasing your overall study hours next week to match your good attendance and goal precision.",
          };
        } else if (goalCompletion >= 45) {
          return {
            heading: "Perfect Attendance Champion!",
            message:
              "You <b>showed up every day</b> with moderate study hours and variable goal achievement. Next week, focus on increasing your study hours while maintaining your perfect attendance.",
          };
        } else {
          return {
            heading: "Perfect Attendance, Target Alignment Needed",
            message:
              "You <b>showed up every day</b>. Focus on increasing your study hours next week and better aligning them with your daily targets to maximize your learning.",
          };
        }
      } else if (diligence >= 45) {
        if (goalCompletion >= 75) {
          return {
            heading: "Attendance Perfect, Hours Need Work",
            message:
              "Your <b>perfect attendance and consistent daily goal achievement</b> show commitment. However, your study hours were below target. Next week, maintain your attendance while increasing study time.",
          };
        } else if (goalCompletion >= 45) {
          return {
            heading: "Attendance Perfect, Hours Need Work",
            message:
              "Your <b>perfect attendance</b> shows commitment, but your study hours and goal achievement were both moderate. Next week, maintain your attendance while focusing on more consistent study quality.",
          };
        } else {
          return {
            heading: "Attendance Perfect, Study Approach Needs Work",
            message:
              "Your <b>perfect attendance</b> shows commitment, but your study hours were below target and rarely aligned with goals. Next week, focus on quality, directed study time that better matches your targets.",
          };
        }
      } else {
        if (goalCompletion >= 75) {
          return {
            heading: "Attendance-Hours Mismatch",
            message:
              "While you <b>showed up every day and hit your targets</b>, your overall study hours were minimal. Next week, increase your study volume while maintaining your good precision.",
          };
        } else if (goalCompletion >= 45) {
          return {
            heading: "Attendance-Hours Mismatch",
            message:
              "While you <b>showed up every day</b>, your study hours were minimal with variable goal achievement. Next week, focus on increasing your study time and consistency.",
          };
        } else {
          return {
            heading: "Attendance-Hours Mismatch",
            message:
              "While you <b>showed up every day</b>, your study hours were minimal and rarely aligned with daily targets. Next week, focus on matching your attendance with adequate, well-directed study hours.",
          };
        }
      }
    }
    // 6/7 days - 86% consistency
    else if (consistency >= 86) {
      if (diligence >= 100) {
        if (goalCompletion >= 75) {
          return {
            heading: "Strong Week Overall!",
            message:
              "Despite missing one day, your <b>study quality was good</b> with consistent goal achievement when you showed up. Next week, aim for perfect attendance while maintaining your study intensity.",
          };
        } else if (goalCompletion >= 45) {
          return {
            heading: "Strong Week Overall!",
            message:
              "Despite missing one day, your <b>study hours were good</b> with progress on daily goals. Next week, aim for perfect attendance while improving goal precision.",
          };
        } else {
          return {
            heading: "Strong Week Overall!",
            message:
              "Despite missing one day, your <b>study hours were good</b>, though your daily goal achievement was inconsistent. Next week, focus on both attendance and better aligning your study with daily targets.",
          };
        }
      } else if (diligence >= 75) {
        if (goalCompletion >= 75) {
          return {
            heading: "Strong Week With Good Effort",
            message:
              "Despite missing one day, you <b>maintained good study hours and goal achievement</b> when present. Next week, focus on perfect attendance while maintaining your effective study approach.",
          };
        } else if (goalCompletion >= 45) {
          return {
            heading: "Strong Week With Room to Improve",
            message:
              "Despite missing one day, you <b>maintained good study hours</b> with variable goal achievement. Next week, focus on perfect attendance and more consistent daily targets.",
          };
        } else {
          return {
            heading: "Attendance Gap, Study Balance Needed",
            message:
              "Despite missing one day, your <b>study hours were good when present</b>, but rarely aligned with goals. Next week, focus on perfect attendance and better target alignment.",
          };
        }
      } else if (diligence >= 56) {
        if (goalCompletion >= 75) {
          return {
            heading: "Attendance Gap, Good Target Focus",
            message:
              "You missed one day, with <b>moderate study hours but good goal achievement</b> when present. Next week, improve both attendance and study hours while maintaining target precision.",
          };
        } else if (goalCompletion >= 45) {
          return {
            heading: "Attendance and Study Improvements Needed",
            message:
              "You <b>missed one day, with moderate study hours</b> and variable goal achievement. Next week, focus on improving both attendance and study consistency.",
          };
        } else {
          return {
            heading: "Multiple Areas for Improvement",
            message:
              "You <b>missed one day, with moderate study hours</b> that rarely aligned with goals. Next week, focus on attendance, study time, and better target alignment.",
          };
        }
      } else if (diligence >= 45) {
        if (goalCompletion >= 75) {
          return {
            heading: "Mixed Performance Week",
            message:
              "Despite <b>missing one day and low study hours</b>, you achieved goals well when studying. Next week, focus on attendance and increasing study hours.",
          };
        } else if (goalCompletion >= 45) {
          return {
            heading: "Multiple Improvement Areas",
            message:
              "You <b>missed one day with below-target study hours</b> and variable goal achievement. Focus on attendance and more effective study time next week.",
          };
        } else {
          return {
            heading: "Significant Improvement Needed",
            message:
              "You <b>missed one day with below-target study hours</b> that rarely aligned with goals. Next week requires improvements in attendance, study time, and target alignment.",
          };
        }
      } else {
        if (goalCompletion >= 75) {
          return {
            heading: "Attendance-Hours Severe Mismatch",
            message:
              "Despite <b>good goal achievement when studying</b>, you missed one day with minimal study hours. Next week, focus on attendance and substantially increasing study time.",
          };
        } else if (goalCompletion >= 45) {
          return {
            heading: "Critical Study Time Deficit",
            message:
              "You <b>missed one day with minimal study hours</b> and variable goal achievement. Next week requires significantly more consistent study time and attendance.",
          };
        } else {
          return {
            heading: "Critical Learning Risk",
            message:
              "You <b>missed one day with minimal study hours</b> that rarely aligned with goals. Next week requires a complete reset of your study approach for success.",
          };
        }
      }
    }
    // 5/7 days - 71% consistency
    else if (consistency >= 71) {
      if (diligence >= 100) {
        if (goalCompletion >= 75) {
          return {
            heading: "Mixed Week, Excellence When Present",
            message:
              "You missed some days, but your <b>effort was good</b> with consistent goal achievement when you did study. Next week, focus on improving your attendance while maintaining your study intensity.",
          };
        } else if (goalCompletion >= 45) {
          return {
            heading: "Mixed Week, Excellence When Present",
            message:
              "You missed some days, but your <b>effort was good</b> when you did study with progress on daily goals. Next week, focus on improving your attendance.",
          };
        } else {
          return {
            heading: "Mixed Week, Target Alignment Needed",
            message:
              "You missed some days, and despite <b>good study hours when present</b>, your daily goal achievement was inconsistent. Next week, focus on attendance and aligning study with targets.",
          };
        }
      } else if (diligence >= 75) {
        if (goalCompletion >= 75) {
          return {
            heading: "Partial Week, Good Effort",
            message:
              "You <b>missed multiple days but maintained good study hours</b> with strong goal achievement when present. Next week, prioritize attendance while continuing your effective study approach.",
          };
        } else if (goalCompletion >= 45) {
          return {
            heading: "Partial Week, Moderate Success",
            message:
              "You <b>missed multiple days with good study hours</b> but variable goal achievement when present. Next week, improve attendance and focus on consistent daily targets.",
          };
        } else {
          return {
            heading: "Attendance and Target Issues",
            message:
              "You <b>missed multiple days with good study hours</b> but poor goal alignment when present. Improve attendance and target focus next week.",
          };
        }
      } else if (diligence >= 56) {
        if (goalCompletion >= 75) {
          return {
            heading: "Attendance Issues, Good Targeting",
            message:
              "You <b>missed multiple days with moderate study hours</b> but good goal achievement when present. Next week, prioritize attendance and increase study time.",
          };
        } else if (goalCompletion >= 45) {
          return {
            heading: "Multiple Areas Need Attention",
            message:
              "You <b>missed multiple days with moderate study hours</b> and variable goal achievement. Next week, focus on regular attendance and more consistent study.",
          };
        } else {
          return {
            heading: "Significant Study Approach Issues",
            message:
              "You <b>missed multiple days with moderate study hours</b> and poor goal alignment. Next week, improve attendance and completely rethink your study approach.",
          };
        }
      } else if (diligence >= 45) {
        if (goalCompletion >= 75) {
          return {
            heading: "Critical Study Time Deficit",
            message:
              "Despite <b>good goal precision when studying</b>, you missed multiple days with minimal study hours. Focus on attendance and increasing study time significantly.",
          };
        } else if (goalCompletion >= 45) {
          return {
            heading: "Concerning Performance Pattern",
            message:
              "You <b>missed multiple days with low study hours</b> and inconsistent goal achievement. Next week requires a major shift in your study commitment.",
          };
        } else {
          return {
            heading: "Critical Learning Risk",
            message:
              "You <b>missed multiple days with low study hours</b> and poor goal alignment. Next week, your study approach needs a complete reset for success.",
          };
        }
      } else {
        if (goalCompletion >= 75) {
          return {
            heading: "Severe Study Time Deficit",
            message:
              "Despite <b>good targeting</b>, you missed multiple days with minimal study effort. Next week requires dramatically improved attendance and study time.",
          };
        } else if (goalCompletion >= 45) {
          return {
            heading: "High Risk Learning Pattern",
            message:
              "You <b>missed multiple days with minimal study effort</b> and inconsistent targeting. Next week requires a complete overhaul of your study approach.",
          };
        } else {
          return {
            heading: "Critical: Learning At Risk",
            message:
              "You <b>missed multiple days with minimal study effort</b> and poor goal alignment. Next week requires an urgent and complete reset of your approach.",
          };
        }
      }
    }
    // 4/7 days - 57% consistency
    else if (consistency >= 57) {
      if (diligence >= 100) {
        if (goalCompletion >= 75) {
          return {
            heading: "Inconsistent Week, Excellent When Present",
            message:
              "Despite missing several days, your <b>effort was excellent</b> with strong goal achievement when present. Next week, focus on improving attendance while maintaining quality.",
          };
        } else if (goalCompletion >= 45) {
          return {
            heading: "Inconsistent Week, Good Hours",
            message:
              "Despite missing several days, your <b>study hours were good</b> when present. Next week, focus on attendance and better goal achievement.",
          };
        } else {
          return {
            heading: "Attendance and Target Issues",
            message:
              "You <b>missed several days</b>, and even when present your daily goal achievement was inconsistent despite good hours. Focus on attendance and better aligning study with targets.",
          };
        }
      } else if (diligence >= 75) {
        if (goalCompletion >= 75) {
          return {
            heading: "Concerning Attendance Pattern",
            message:
              "You <b>missed several days but maintained good focus</b> when present. Next week, prioritize regular attendance while continuing your effective study approach.",
          };
        } else if (goalCompletion >= 45) {
          return {
            heading: "Multiple Study Concerns",
            message:
              "You <b>missed several days with good hours</b> but inconsistent goal achievement when present. Next week requires better attendance and more focused study.",
          };
        } else {
          return {
            heading: "Major Learning Gaps Forming",
            message:
              "You <b>missed several days with ineffective study</b> even when present. Next week requires a complete overhaul of your attendance and study approach.",
          };
        }
      } else if (diligence >= 56) {
        if (goalCompletion >= 75) {
          return {
            heading: "Erratic Study Pattern",
            message:
              "Despite <b>good goal achievement when studying</b>, you missed several days with moderate hours. Focus on consistent attendance and increasing study time.",
          };
        } else if (goalCompletion >= 45) {
          return {
            heading: "Concerning Learning Pattern",
            message:
              "You <b>missed several days with moderate effort</b> and variable goal achievement. Next week requires significant improvements in consistency.",
          };
        } else {
          return {
            heading: "Major Study Approach Issues",
            message:
              "You <b>missed several days with unfocused study</b> when present. Next week requires a fundamentally different approach to succeed.",
          };
        }
      } else if (diligence >= 45) {
        if (goalCompletion >= 75) {
          return {
            heading: "High Risk Learning Pattern",
            message:
              "Despite <b>hitting goals when studying</b>, you missed several days with minimal effort. Next week requires dramatically better attendance and study time.",
          };
        } else if (goalCompletion >= 45) {
          return {
            heading: "Critical: Learning Progress Stalled",
            message:
              "You <b>missed several days with minimal effort</b> and inconsistent goals. This creates serious learning deficits. An immediate and complete approach change is required.",
          };
        } else {
          return {
            heading: "Critical: Learning Goals At Risk",
            message:
              "Your <b>limited attendance and ineffective study</b> has created serious learning deficits. An immediate and complete approach change is required.",
          };
        }
      } else {
        if (goalCompletion >= 0) { // Any goal completion level
          return {
            heading: "Critical: Learning Goals Unlikely",
            message:
              "Your limited attendance and minimal study effort make learning goals unlikely. <b>An urgent and complete reset is required immediately.</b>",
          };
        }
      }
    }
    // 3/7 days - 43% consistency
    else if (consistency >= 43) {
      if (diligence >= 75) {
        if (goalCompletion >= 75) {
          return {
            heading: "Serious Attendance Deficit",
            message:
              "Despite <b>good study quality when present</b>, you missed most of the week. This creates significant learning gaps. Focus on consistent attendance next week.",
          };
        } else if (goalCompletion >= 0) { // Any other goal completion
          return {
            heading: "Critical: Learning Progress At Risk",
            message:
              "You <b>missed most of the week</b> with varying study quality. This pattern makes learning goals difficult to achieve. A complete approach reset is needed.",
          };
        }
      } else if (diligence >= 0) { // Any diligence level below 75%
        if (goalCompletion >= 0) { // Any goal completion level
          return {
            heading: "Critical: Learning Goals At Severe Risk",
            message:
              "Your minimal attendance and effort have created major learning deficits. <b>An urgent and complete intervention is required.</b>",
          };
        }
      }
    }
    // 2/7 days - 29% consistency
    else if (consistency >= 29) {
      return {
        heading: "Severe Learning Deficit",
        message:
          "You missed most of the week with varying effort when present. <b>This creates a learning deficit.</b> Next week requires a complete change of your study approach.",
      };
    }
    // 1/7 days - 14% consistency
    else if (consistency >= 14) {
      return {
        heading: "Critical: Week Effectively Lost",
        message:
          "<b>You missed almost the entire week with minimal learning progress.</b> Next week requires a fundamental change in your approach.",
      };
    }
    // 0/7 days - 0% consistency
    else {
      return {
        heading: "Critical: Zero Progress Week",
        message:
          "<b>You didn't study at all this week.</b> If this continues, it will seriously harm your learning goals. For next week, you need to start fresh and recommit to your studies.",
      };
    }
  }
  
}
